import dayjs from "dayjs";
import AdvancedFormat from "dayjs/plugin/advancedFormat";
import { MouseEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Customized,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AxisDomain, AxisInterval } from "recharts/types/util/types";

import { IPatientBaseline } from "../../@types/Baselines";
import { MeasurementTypes } from "../../@types/Measurements";
import useIsMobile from "../../hooks/useIsMobile";
import {
  DATE_FILTER_ALL,
  DateFilterOptionType,
} from "../../pages/PatientPage/components/MeasurementModal/components/CustomOptionsBar";
import {
  calculateLinearRegression,
  getGraphSeparators,
} from "../../utils/chartUtils";
import {
  getBloodPressurePairedValues,
  getDataUnformattedtimeAndValues,
} from "../../utils/measurementsUtils";
import {
  BaselineTooltipOverlay,
  IBaselineSegment,
} from "../BaselineTooltip/BaselineTooltipOverlay";
import { ChartConfig, ChartData } from "../ChartCard/ChartCard.types";
import { renderChartEmptyState } from "../ChartEmptyState";
import { CustomRechartsDot } from "../CustomRechartsDot/CustomRechartsDot";
import { CustomRechartsTooltip } from "../CustomRechartsTooltip/CustomRechartsTooltip";
import { CustomModalChartDot } from "./CustomModalChartDot/CustomModalChartDot";
import { CustomModalChartLegend } from "./CustomModalChartLegend/CustomModalChartLegend";
import { CustomModalChartTick } from "./CustomModalChartTick/CustomModalChartTick";
import { setSelectedPoint } from "./selectedPointStore";

type MeasurementPayloadType = {
  fvc: number;
  time: string;
  unformattedTime: string;
  spirometryTestIds: number[];

  cx: number;
  cy: number;
};

type ModalChartProps = {
  xConfig?: {
    label?: any;
    dataKey?: string;
    axisSize?: number;
    minTickGap?: number;
    interval?: AxisInterval;
    allowDecimals?: boolean;
    type?: "number" | "category";
    ticks?: Array<number | string>;
    tickFormatter?: (value: string) => string;
  };
  yConfig: {
    axisSize?: number;
    domain?: AxisDomain;
    label?: any;
  };
  data: Map<string, ChartData[]> | Map<string, any[]>;
  mainValues: any;
  diastolicMainValues: any;
  width: string | number;
  height: string | number;
  disableTooltip?: boolean;
  chartConfig: ChartConfig[];
  onRowClick?: (payload: MeasurementPayloadType) => void;
  dateInterval?: DateFilterOptionType;
  isSpirometry?: boolean;
  isPinnedGraph?: boolean;
  patientBaselines: IPatientBaseline[];
  minValue?: number;
  maxValue?: number;
  measurementType?: MeasurementTypes;
  showTrendLine: boolean;
  isEmpty?: boolean;
  emptyMessage?: string;
  emptySecondaryMessage?: string;
};

export function ModalChart({
  data,
  mainValues,
  width,
  height,
  diastolicMainValues,
  xConfig,
  yConfig,
  onRowClick,
  chartConfig,
  showTrendLine,
  disableTooltip,
  dateInterval,
  isSpirometry,
  isPinnedGraph,
  patientBaselines,
  minValue,
  maxValue,
  measurementType,
  isEmpty,
  emptyMessage,
  emptySecondaryMessage,
}: ModalChartProps) {
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const [isPointerOnPoint, setIsPointerOnPoint] = useState(false);

  dayjs.extend(AdvancedFormat);

  let referenceTicks: number[] = [];
  const { yKey } = chartConfig[0] ?? {};

  const isAllWithNoData = isEmpty && dateInterval?.id === DATE_FILTER_ALL;
  const allEmptyAnchor = isAllWithNoData
    ? dateInterval?.endDate ?? dayjs()
    : undefined;

  const getSeparatorsMinDate = () => {
    if (isAllWithNoData) return allEmptyAnchor;
    if (isEmpty) return dateInterval?.startDate;
    return mainValues.minDate;
  };

  const getSeparatorsMaxDate = () => {
    if (isAllWithNoData) return allEmptyAnchor;
    if (isEmpty) return dateInterval?.endDate;
    return mainValues.maxDate;
  };

  const {
    graphSeparators,
    dates,
    separatorCounter,
    tickFormat,
    viewType,
    isTickMultipleLine,
  } = getGraphSeparators(
    dateInterval,
    getSeparatorsMinDate(),
    getSeparatorsMaxDate()
  );

  const tickXFormatter = (value: string) => {
    if (!isEmpty && (!data || !data.size)) return "";
    if (xConfig?.dataKey) return value;

    if (
      graphSeparators.includes(Number(value)) ||
      referenceTicks.includes(Number(value))
    ) {
      if (tickFormat === "Q") {
        return (
          value &&
          `Q${dayjs(value).format(tickFormat)}, ${dayjs(value).format("YY")}`
        );
      }

      return value && dayjs(value).format(tickFormat);
    }

    return "";
  };

  const handleOnClickOverActiveDots = (selectedPointInfo: any) => {
    if (onRowClick && isSpirometry) {
      setSelectedPoint(selectedPointInfo);
      onRowClick(selectedPointInfo as MeasurementPayloadType);
    }
  };

  const handleXDomain = () => {
    if (!dateInterval || !data) return [0, 1];

    if (isAllWithNoData && allEmptyAnchor) {
      const dayStart = allEmptyAnchor.startOf("day");
      return [dayStart.valueOf(), dayStart.add(1, "day").valueOf()];
    }

    const { minDate, maxDate } = mainValues;
    let { startDate, endDate } = dateInterval;

    if (minDate && maxDate && endDate.diff(startDate, "year") > 5) {
      startDate = minDate;
      endDate = maxDate;
    }

    if (endDate.diff(startDate, "months") > 11) {
      endDate = endDate.add(30, "day");
    }

    return [startDate.valueOf(), endDate.valueOf()];
  };

  const refs: number[] = [
    ...graphSeparators,
    ...dates.filter((_, index) => index % separatorCounter === 0),
  ];

  referenceTicks = [...refs];

  const allSourcesData = Array.from(data.values()).flat();

  const handleYAxisDomain = () => {
    if (isEmpty) {
      if (yConfig.domain) {
        const yConfigs = yConfig.domain as any[];
        if (
          typeof yConfigs[0] !== "function" &&
          typeof yConfigs[1] !== "function"
        ) {
          return [yConfigs[0], yConfigs[1]];
        }
      }
      if (minValue != null && maxValue != null) {
        return [minValue, maxValue];
      }
      return ["auto", "auto"];
    }

    let allValues: number[] = [];

    if (yKey === "bp_systolic") {
      const { yValues } = getBloodPressurePairedValues(data);
      allValues = [
        ...yValues.systolic.filter(
          (value): value is number => value !== undefined
        ),
        ...yValues.diastolic.filter(
          (value): value is number => value !== undefined
        ),
      ];
    } else {
      allValues = getDataUnformattedtimeAndValues(data, yKey).yValues;
    }

    const minData = Math.min(...allValues);
    const maxData = Math.max(...allValues);

    const baselines = patientBaselines.map((item) => item.baseline);

    const minBaseline = Math.min(...baselines);
    const maxBaseline = Math.max(...baselines);

    let yConfigMinValue;
    let yConfigMaxValue;
    if (yConfig.domain) {
      const yConfigs = yConfig.domain as any[];

      if (typeof yConfigs[0] === "function") {
        yConfigMinValue = yConfigs[0](minData);
      } else {
        [yConfigMinValue, yConfigMaxValue] = yConfigs;
      }
    }

    const min = Math.min(
      minData,
      minBaseline - 1,
      Number.isInteger(yConfigMinValue) ? yConfigMinValue : minData
    );
    const max = Math.max(
      maxData,
      maxBaseline + 1,
      Number.isInteger(yConfigMaxValue) ? yConfigMaxValue : minData
    );

    if (
      !data.size &&
      patientBaselines.length > 0 &&
      minValue != null &&
      !Number.isNaN(minValue) &&
      maxValue != null &&
      !Number.isNaN(maxValue)
    ) {
      return [Math.min(min, minValue), Math.max(max, maxValue)];
    }

    if (!yConfig.domain) {
      return ["auto", "auto"];
    }

    return [Math.floor(min), Math.ceil(max)];
  };

  const calculateMeasurementLinearRegression = () => {
    const { xValues, yValues } = getDataUnformattedtimeAndValues(data, yKey);

    return calculateLinearRegression(xValues, yValues);
  };

  const calculateBloodPressureLinearRegression = () => {
    const { xValues, yValues } = getBloodPressurePairedValues(data);

    const filteredSystolic = yValues.systolic.filter(
      (v): v is number => v !== undefined
    );
    const filteredDiastolic = yValues.diastolic.filter(
      (v): v is number => v !== undefined
    );

    return {
      diastolic: calculateLinearRegression(xValues, filteredDiastolic),
      systolic: calculateLinearRegression(xValues, filteredSystolic),
    };
  };

  useEffect(() => {
    return () => {
      setSelectedPoint(null);
    };
  }, []);

  // Recharts hides the tooltip on the point's mouseleave, which never fires when the
  // point re-mounts under the pointer, so visibility follows what is under the pointer.
  const handleChartMouseMove = (event: MouseEvent<HTMLDivElement>) => {
    const isOnPoint =
      (event.target as Element).closest(".recharts-scatter-symbol") !== null;

    if (isOnPoint !== isPointerOnPoint) setIsPointerOnPoint(isOnPoint);
  };

  const baselineSegments: IBaselineSegment[] = useMemo(() => {
    return patientBaselines.map((patientBaseline) => {
      const handleEndSegmentXAxis = () => {
        if (patientBaseline.archivedDate !== undefined) {
          return dayjs(patientBaseline.archivedDate).valueOf();
        }

        const archiveDateDayjs = dayjs(patientBaseline.archivedDate);

        if (dayjs().diff(archiveDateDayjs, "day") < 30) {
          return dateInterval?.endDate.add(30, "day").valueOf() ?? 0;
        }

        return dateInterval?.endDate.valueOf() ?? 0;
      };

      const handleStartSegmentXAxis = () => {
        if (dayjs(patientBaseline.created).diff(dateInterval?.startDate) < 0) {
          return dateInterval?.startDate.valueOf() ?? 0;
        }

        return dayjs(patientBaseline.created).valueOf();
      };

      return {
        baseline: patientBaseline,
        xStart: handleStartSegmentXAxis(),
        xEnd: handleEndSegmentXAxis(),
        yValue: patientBaseline.baseline,
      };
    });
  }, [patientBaselines, dateInterval]);

  return (
    <div
      style={{
        position: "relative",
        width,
        height,
        minWidth: isEmpty ? 600 : undefined,
      }}
      onMouseMove={handleChartMouseMove}
      onMouseLeave={() => setIsPointerOnPoint(false)}
    >
      <ResponsiveContainer
        debounce={10}
        width="100%"
        height="100%"
        minWidth={isPinnedGraph ? 658 : undefined}
        // A resize moves the points and recharts rebuilds the tooltip on the first one.
        onResize={() => setIsPointerOnPoint(false)}
      >
        {/* No chart-level `data` (each Scatter has its own): when it changes, recharts
            rebuilds the tooltip from the axis and snaps it to the first point. */}
        <ScatterChart margin={{ right: 20, top: 8 }}>
          {!disableTooltip && (
            <Tooltip
              active={isPointerOnPoint ? undefined : false}
              cursor={false}
              content={<CustomRechartsTooltip data={allSourcesData} />}
              wrapperStyle={{ outline: "none" }}
            />
          )}
          <CartesianGrid strokeDasharray="2" vertical={false} />
          <YAxis
            tickMargin={8}
            stroke="#D8D8D8"
            allowDecimals={measurementType === "cf_dna"}
            domain={handleYAxisDomain()}
            width={yConfig.axisSize || 48}
            tick={{
              fill: isEmpty ? "#B5B5B6" : "#727272",
              fontSize: isMobile ? "10px" : "12px",
              fontWeight: 400,
            }}
          />
          <XAxis
            tickMargin={14}
            stroke="#D8D8D8"
            type="number"
            ticks={viewType === "yearly" ? graphSeparators : dates}
            domain={handleXDomain()}
            tickFormatter={xConfig?.tickFormatter || tickXFormatter}
            allowDuplicatedCategory={false}
            height={xConfig?.axisSize || 48}
            dataKey="unformattedTime"
            allowDecimals={xConfig?.allowDecimals}
            minTickGap={xConfig?.minTickGap || 100}
            interval={xConfig?.interval || 0}
            tick={
              isTickMultipleLine ? (
                <CustomModalChartTick isEmpty={isEmpty} />
              ) : (
                {
                  fill: isEmpty ? "#B5B5B6" : "#727272",
                  fontSize: isMobile ? "10px" : "12px",
                  fontWeight: "400",
                }
              )
            }
            angle={refs.length >= 15 || isMobile ? 30 : 0}
          />

          {refs.map((ref) => (
            <ReferenceLine
              key={ref.toString()}
              x={ref}
              stroke="#D8D8D8"
              strokeDasharray="8 1000"
            />
          ))}

          {showTrendLine && (
            <>
              <ReferenceLine
                stroke="#285FCA"
                strokeDasharray="15 15"
                strokeWidth={2}
                segment={
                  yKey !== "bp_systolic"
                    ? calculateMeasurementLinearRegression()
                    : calculateBloodPressureLinearRegression().systolic
                }
                ifOverflow="hidden"
              />
              <ReferenceLine
                stroke="#285FCA"
                strokeDasharray="15 15"
                strokeDashoffset={15}
                strokeOpacity={0.57}
                strokeWidth={2}
                segment={
                  yKey !== "bp_systolic"
                    ? calculateMeasurementLinearRegression()
                    : calculateBloodPressureLinearRegression().systolic
                }
                ifOverflow="hidden"
              />
              {yKey === "bp_systolic" && (
                <>
                  <ReferenceLine
                    stroke="#285FCA"
                    strokeDasharray="15 15"
                    strokeOpacity={0.4}
                    strokeWidth={2}
                    segment={
                      yKey !== "bp_systolic"
                        ? calculateMeasurementLinearRegression()
                        : calculateBloodPressureLinearRegression().diastolic
                    }
                    ifOverflow="hidden"
                  />
                  <ReferenceLine
                    stroke="#285FCA"
                    strokeDasharray="15 15"
                    strokeDashoffset={15}
                    strokeOpacity={0.57}
                    strokeWidth={2}
                    segment={
                      yKey !== "bp_systolic"
                        ? calculateMeasurementLinearRegression()
                        : calculateBloodPressureLinearRegression().diastolic
                    }
                    ifOverflow="hidden"
                  />
                </>
              )}
            </>
          )}

          {baselineSegments.length > 0 &&
            baselineSegments.map((seg) => (
              <ReferenceLine
                key={`baseline-ref-${seg.baseline.id}`}
                stroke={seg.baseline.isISHLTBaseline ? "#754164" : "#58C488"}
                strokeDasharray="10 8"
                strokeWidth={2}
                strokeOpacity={isEmpty ? 0.4 : 1}
                segment={[
                  { x: seg.xStart, y: seg.yValue },
                  { x: seg.xEnd, y: seg.yValue },
                ]}
                ifOverflow="hidden"
              />
            ))}

          <Legend
            content={
              <CustomModalChartLegend
                mainValues={mainValues}
                diastolicMainValues={diastolicMainValues}
                baseline={
                  patientBaselines.filter((pb) => pb.isISHLTBaseline === false)
                    .length > 0
                }
                ishltBaseline={
                  patientBaselines.filter((pb) => pb.isISHLTBaseline === true)
                    .length > 0
                }
                trendLine={showTrendLine}
                isBloodPressure={measurementType === "bp_systolic"}
                chartConfig={chartConfig}
              />
            }
          />

          {chartConfig.map((currentChartConfig: ChartConfig, index: number) => {
            const {
              yKey,
              stroke,
              color,
              dotShape,
              isDotClickable,
              selectedSpirometryTestId,
              lineOpacity,
              source,
            } = currentChartConfig;

            const dataSource = source ? data.get(`${yKey}|${source}`) : [];

            return (
              <Scatter
                key={`${yKey}_${index}_${color}`}
                fill={color}
                line={
                  !showTrendLine
                    ? {
                        stroke: color,
                        strokeWidth: stroke || 1.4,
                        opacity: lineOpacity,
                      }
                    : undefined
                }
                lineJointType="monotoneX"
                isAnimationActive={false}
                shape={
                  isDotClickable ? (
                    <CustomModalChartDot
                      color={color}
                      onDotClick={handleOnClickOverActiveDots}
                      selectedSpirometryTestId={selectedSpirometryTestId}
                    />
                  ) : (
                    <CustomRechartsDot fill={color} shape={dotShape} />
                  )
                }
                dataKey={yKey}
                data={dataSource}
                name={source}
              />
            );
          })}

          {!isEmpty && baselineSegments.length > 0 && (
            <Customized
              component={
                <BaselineTooltipOverlay baselineSegments={baselineSegments} />
              }
            />
          )}

          {isEmpty &&
            renderChartEmptyState({
              message: emptyMessage ?? t("chart_noDataForRange"),
              secondaryMessage: emptySecondaryMessage,
            })}
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
}
